import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ScriptService } from './script.service';

export interface SubscriptionTier {
  id: number;
  name: string;
  color: string;
}

export const tiers: { [key: string]: SubscriptionTier } = {
  "-2": {
    id: -2,
    name: "Payment Declined",
    color: "#ff0000"
  },
  "-1": {
    id: -1,
    name: "No Membership",
    color: "#ff8800"
  },
  "0": {
    id: 0,
    name: "Certified Jar Supporter",
    color: "#6897da"
  },
  "1": {
    id: 1,
    name: "Extra Jar Supporter",
    color: "#43867c"
  },
  "2": {
    id: 2,
    name: "Super Jar Supporter",
    color: "#369876"
  },
  "3": {
    id: 3,
    name: "VIP Supporter",
    color: "#d4843d"
  }
}

export interface AccountConnections {
  discord: ({
    id: string;
    avatar: string;
    username: string;
  }|null),
  vrchat: ({
    id: string;
    username: string;
    avatar: string;
  }|null),
  patreon: ({
    id: string;
    username: string;
    avatar: string;
    subscriptionTier: SubscriptionTier;
    overridden: boolean;
    email?: string;
    expire?: string;
    connected_at?: string;
  }|null)
}

export interface Account {
  id: number;
  username: string;
  staff: boolean;
  type?: 'user' | 'guest';
  connections: AccountConnections;
  notification_preferences?: number; // Bitflag: 1 = Script status changed, 2 = Update accepted, 4 = Milestone reached
  settings?: {
    aaSubmitAs: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AccountService {

  api: ApiService = inject(ApiService);
  scriptService: ScriptService = inject(ScriptService);
  account: BehaviorSubject<Account|null|undefined> = new BehaviorSubject<Account|null|undefined>(undefined);

  loadAccount() {
    this.api.get("account/get.php").subscribe((data: any) => {

      console.log(data)

      if (data.code != 200) {
        this.account.next(null);
        return;
      }

      let a = data.account;

      if (!a) {
        this.account.next(null);
        return;
      }

      let vrchat_connection;
      if (a.vrchat_id) {
        vrchat_connection = {
          id: a.vrchat_id,
          username: a.vrchat_username,
          avatar: a.vrchat_avatar
        }
      } else {
        vrchat_connection = null;
      }

      if (a.patreon_tier === null) {
        a.patreon_tier = -1;
      }

      let patreon_connection;
      if (a.patreon_id) {
        patreon_connection = {
          id: a.patreon_id,
          username: a.patreon_username,
          avatar: a.patreon_avatar,
          subscriptionTier:  tiers[a.patreon_tier.toString()],
          overridden: a.patreon_overridden
        }
      } else {
        patreon_connection = null;
      }

      let account: Account = {
        id: a.id,
        staff: a.staff == 1,
        username: a.username,
        connections: {
          discord: {
            id: a.discord_id,
            username: a.discord_username,
            avatar: a.discord_avatar.slice(0, 4) == "http" ? a.discord_avatar : "https://jar.games" + a.discord_avatar
          },
          vrchat: vrchat_connection,
          patreon: patreon_connection
        },
        notification_preferences: a.notification_preferences ?? 0,
        settings: {
          aaSubmitAs: a.aaSubmitAs ?? "auto"
        }
      }


      console.log(account);

      this.account.next(account);
    });
  }

  constructor() {
    this.loadAccount();

    this.account.subscribe(account => { 
      console.log("[Account] Account updated", account);
    });

    (window as any).accountService = this;
  }

  isSubscriber(): boolean {
    if (!this.account.value) return false;
    if (!this.account.value.connections.patreon) return false;
    return this.account.value.connections.patreon.subscriptionTier.id >= 0;
  }

  connectVRChat(value: string): Observable<any> {

    // Figure out if the value is a VRChat ID or a username
    let action: string;
    if (value.startsWith('https://vrchat.com/home/user/')) {
      value = value.split('https://vrchat.com/home/user/')[1];
      return this.api.post("account/connect_vrchat.php", {
        action: "connect_id",
        vrchat_id: value
      }).pipe(map((data: any) => {
        console.log(data);
        if (data.code == 200) {
          this.loadAccount();
        }
        return data;
      }));
    } else {
      return this.api.post("account/connect_vrchat.php", {
        action: "connect_displayname",
        display_name: value
      }).pipe(map((data: any) => {
        console.log(data);
        if (data.code == 200) {
          this.loadAccount();
        }
        return data;
      }));
    }

  }

  disconnectPatreon(): Observable<any> {
    return this.api.post("account/disconnect_patreon.php", {
      action: "disconnect"
    }).pipe(map((data: any) => {
      if (data.code == 200) {
        let newAccount = this.account.value;
        if (newAccount) {
          newAccount.connections.patreon = null;
          this.account.next(newAccount);
        }
      }
      return data;
    }));
  }

  disconnectVRChat(): Observable<any> {
    return this.api.post("account/disconnect_vrchat.php", {
      action: "disconnect"
    }).pipe(map((data: any) => {
      if (data.code == 200) {
        let newAccount = this.account.value;
        if (newAccount) {
          newAccount.connections.vrchat = null;
          this.account.next(newAccount);
        }
      }
      return data;
    }));
  }

  /**
   * @param state solved proof-of-work (see LoginComponent), Discord hands it back on the redirect
   */
  login(state: string) {

    window.location.href = 'https://discord.com/oauth2/authorize?client_id=870088840344395797&response_type=code&redirect_uri=https%3A%2F%2Fjar.games%2Flogin&scope=identify&state=' + encodeURIComponent(state);
  }

  logout(): Observable<any> {
    return this.api.post("account/logout.php", {
      action: "logout"
    }).pipe(map((data: any) => {
      console.log(data);
      if (data.code == 200) {
        // Clear the account from the service
        this.account.next(null);
        this.scriptService.ownScripts = [];
      }
      return data;
    }));
  }
}
